import React, { useState, useRef } from "react"; // Include useRef here
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  sendEmailVerification,
} from "firebase/auth";
import ReCAPTCHA from "react-google-recaptcha";
import GoogleLogo from "../logos/google.png";
import { logFingerprintToBackend } from "../utils/FingerprintService";

function AuthPage() {
  // State variables
  const [isSignUp, setIsSignUp] = useState(false);
  const [emailOrUsername, setEmailOrUsername] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dob, setDob] = useState("");
  const [phone, setPhone] = useState("");
  const [username, setUsername] = useState("");
  const [captchaValue, setCaptchaValue] = useState(null);
  const [error, setError] = useState("");
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [emailListOptIn, setEmailListOptIn] = useState(false);
  const navigate = useNavigate();
  const captchaRef = useRef(null); // Reference for the reCAPTCHA

  // Handle CAPTCHA change
  const onCaptchaChange = (value) => {
    setCaptchaValue(value);
    console.log("CAPTCHA solved, token:", value);
  };

  // Handle Google Login
  const handleGoogleLogin = async () => {
    const provider = new GoogleAuthProvider();
    try {
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const idToken = await user.getIdToken();

      const response = await fetch("http://localhost:2000/api/auth/google-login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });

      const data = await response.json();

      if (response.ok) {
        // Log fingerprint for Google login
        await logFingerprintToBackend(user.uid);

        if (data.profileComplete) {
          navigate("/my-predictions");
        } else {
          navigate("/complete-profile");
        }
      } else {
        setError(data.error || "Failed to log in with Google.");
      }
    } catch (err) {
      console.error("Error during Google login:", err);
      setError("Failed to log in with Google. Please try again.");
    }
  };

  // Handle Sign Up
  const handleSignUp = async (e) => {
    e.preventDefault();
    setError("");

    if (!agreeToTerms) {
      setError("You must agree to the privacy policy and terms of conditions.");
      if (captchaRef.current) {
        captchaRef.current.reset();
        setCaptchaValue(null);
      }
      return;
    }

    if (!captchaValue) {
      setError("Please complete the CAPTCHA.");
      if (captchaRef.current) {
        captchaRef.current.reset();
        setCaptchaValue(null);
      }
      return;
    }

    try {
      // Verify CAPTCHA with backend
      const captchaResponse = await fetch("http://localhost:2000/api/auth/verify-recaptcha", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: captchaValue }),
      });

      const captchaResult = await captchaResponse.json();

      if (!captchaResponse.ok || !captchaResult.message) {
        setError("CAPTCHA verification failed. Please try again.");
        if (captchaRef.current) {
          captchaRef.current.reset();
          setCaptchaValue(null);
        }
        return;
      }

      // Perform validations by calling the backend
      const validationResponse = await fetch("http://localhost:2000/api/auth/complete-profile", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username,
          firstName,
          lastName,
          dob,
          phone,
          emailListOptIn,
        }),
      });

      const validationResult = await validationResponse.json();

      if (!validationResponse.ok) {
        setError(validationResult.error || "Validation failed.");
        if (captchaRef.current) {
          captchaRef.current.reset();
          setCaptchaValue(null);
        }
        return;
      }

      // Create user with Firebase Authentication
      const userCredential = await createUserWithEmailAndPassword(auth, emailOrUsername, password);
      const user = userCredential.user;

      // Send email verification
      await sendEmailVerification(user);

      // Get the user's ID token
      const idToken = await user.getIdToken();

      // Save the user profile data in Firestore via the backend
      const response = await fetch("http://localhost:2000/api/auth/complete-profile", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          userId: user.uid,
          username,
          firstName,
          lastName,
          dob,
          phone,
          emailListOptIn,
          visitorId: null,
          metadata: null,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || "Failed to complete profile.");
        if (captchaRef.current) {
          captchaRef.current.reset();
          setCaptchaValue(null);
        }
        return;
      }

      // Log fingerprint for the new user (if applicable)
      await logFingerprintToBackend(user.uid);

      setError("Verification email sent! Please check your inbox.");
      navigate("/verify-email");

    } catch (err) {
      if (err.code === 'auth/email-already-in-use') {
        setError('Email already in use. Please use another email.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Please choose a stronger password.');
      } else {
        setError(err.message || "An unexpected error occurred. Please try again.");
      }
      if (captchaRef.current) {
        captchaRef.current.reset();
        setCaptchaValue(null);
      }
    }
  };

  // Handle Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");

    try {
      const userCredential = await signInWithEmailAndPassword(auth, emailOrUsername, password);
      const user = userCredential.user;
      const idToken = await user.getIdToken();

      const response = await fetch("http://localhost:2000/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ idToken }),
      });

      const data = await response.json();

      if (response.ok) {
        // Log fingerprint for login
        await logFingerprintToBackend(user.uid);

        navigate("/my-predictions");
      } else {
        setError(data.error || "Failed to log in.");
      }
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message || "Failed to log in. Please try again.");
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-indigo-900 via-gray-800 to-gray-900 text-white px-4 sm:px-6 md:px-8">
      <div className="flex-grow flex items-center justify-center pt-24 pb-12">
        <div className="w-full max-w-sm sm:max-w-md bg-gray-800 p-4 sm:p-6 rounded-lg shadow-lg">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-center mb-4">
            {isSignUp ? "Sign Up" : "Login"}
          </h2>
          {error && <p className="text-red-500 text-center mb-4">{error}</p>}

          <form
            className="space-y-3"
            onSubmit={isSignUp ? handleSignUp : handleLogin}
          >
            {isSignUp && (
              <>
                <div>
                  <label className="block text-gray-300 mb-1">Username</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-1">First Name</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-1">Last Name</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
              </>
            )}
            <div>
              <label className="block text-gray-300 mb-1">
                {isSignUp ? "Email" : "Email or Username"}
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={emailOrUsername}
                onChange={(e) => setEmailOrUsername(e.target.value)}
                required
              />
            </div>
            <div>
              <label className="block text-gray-300 mb-1">Password</label>
              <input
                type="password"
                className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {isSignUp && (
              <>
                <div>
                  <label className="block text-gray-300 mb-1">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={dob}
                    onChange={(e) => setDob(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-gray-300 mb-1">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    className="w-full px-3 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                  />
                </div>
                <div className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    checked={emailListOptIn}
                    onChange={(e) => setEmailListOptIn(e.target.checked)}
                  />
                  <label className="ml-2 block text-gray-300">
                    Add me to the email list
                  </label>
                </div>
                <div className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    required
                  />
                  <label className="ml-2 block text-gray-300">
                    I agree to the{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:underline"
                    >
                      Privacy Policy
                    </a>{" "}
                    and{" "}
                    <a
                      href="/terms-and-conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:underline"
                    >
                      Terms of Conditions
                    </a>
                  </label>
                </div>
                <div className="flex justify-center mt-2">
                  <ReCAPTCHA
                    ref={captchaRef} // Attach the ref here
                    sitekey="6LeJ5IAqAAAAAIuyUELaH2H6o6x2dds2PvvKdPiG" // Replace with your actual site key
                    onChange={onCaptchaChange}
                  />
                </div>
              </>
            )}
            <button
              type="submit"
              className="w-full bg-indigo-500 py-2 rounded-lg text-white font-semibold hover:bg-indigo-600 transition duration-200 shadow-md"
            >
              {isSignUp ? "Sign Up" : "Login"}
            </button>
            <div className="flex items-center justify-center my-4">
              <div className="border-t border-gray-500 w-1/4"></div>
              <span className="text-gray-400 mx-2">or</span>
              <div className="border-t border-gray-500 w-1/4"></div>
            </div>
            <button
              type="button"
              onClick={handleGoogleLogin}
              className="flex items-center justify-center w-full bg-white text-gray-700 border border-gray-300 py-2 rounded-lg shadow-md hover:bg-gray-100 transition duration-200"
            >
              <img
                src={GoogleLogo}
                alt="Google Logo"
                className="w-5 h-5 mr-2"
              />
              <span>
                {isSignUp ? "Sign Up with Google" : "Login with Google"}
              </span>
            </button>
          </form>
          <div className="mt-4 text-center">
            <span
              onClick={() => setIsSignUp(!isSignUp)}
              className="text-blue-400 hover:underline cursor-pointer"
            >
              {isSignUp
                ? "Already have an account? Login here."
                : "Don't have an account? Sign up here."}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AuthPage;
